import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormModal } from './FormModal';
import { RichTextEditor } from './RichTextEditor';
import { ClauseTypeModal, ClauseTypeResult } from './ClauseTypeModal';
import { FileUploadInput } from './FileUploadInput';
import { ClauseListItem } from '../shared/playbook-types';
import * as api from '../services/apiClient';
import { logger } from '../utils/logger';
import { cleanEmptyHtml } from '../shared/htmlUtils';
import { useImpersonation } from '../context/ImpersonationContext';

interface SortableClauseItemProps {
  clause: { id: number; name: string };
  index: number;
  onRemove: (clauseId: number) => void;
}

const SortableClauseItem = React.memo(({ clause, index, onRemove }: SortableClauseItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(clause.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`clause-list-item ${isDragging ? 'dragging' : ''}`}
    >
      <span
        {...attributes}
        {...listeners}
        className="clause-drag-handle"
        title="Glisser pour réordonner"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </span>
      <span className="clause-name">{clause.name}</span>
      <span className="clause-order-badge">{index + 1}</span>
      <button
        type="button"
        className="clause-remove-btn"
        onClick={() => onRemove(clause.id)}
        title="Retirer cette clause"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  );
});

SortableClauseItem.displayName = 'SortableClauseItem';

export interface ContractTypeFormData {
  name: string;
  directives: string;
  clauses: { id: number; name: string }[];
  frameworkS3Key: string | null;
  frameworkFileName: string | null;
}

export interface ContractTypeResult {
  id: number;
  name: string;
  note: string;
  clause_types: { id: number; name: string; order?: number }[];
}

interface ContractTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initialData?: ContractTypeFormData;
  contractId?: number;
  availableClauses: ClauseListItem[];
  onSubmitSuccess?: (data: ContractTypeResult) => void;
  onRefreshNeeded?: () => void;
  onClausesRefreshNeeded?: () => void;
}

const defaultFormData: ContractTypeFormData = {
  name: '',
  directives: '',
  clauses: [],
  frameworkS3Key: null,
  frameworkFileName: null,
};

export const ContractTypeModal: React.FC<ContractTypeModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  contractId,
  availableClauses,
  onSubmitSuccess,
  onRefreshNeeded,
  onClausesRefreshNeeded,
}) => {
  const { activeAlias } = useImpersonation();
  const previousAliasRef = useRef(activeAlias);
  const [formData, setFormData] = useState<ContractTypeFormData>(defaultFormData);
  const [originalClauses, setOriginalClauses] = useState<{ id: number; name: string }[]>([]);
  const [originalFrameworkKey, setOriginalFrameworkKey] = useState<string | null>(null);
  const [originalFrameworkFileName, setOriginalFrameworkFileName] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [createClauseModalOpen, setCreateClauseModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isInReplaceMode, setIsInReplaceMode] = useState(false);
  const [showFrameworkConfirm, setShowFrameworkConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close modal if impersonation changes while open
  useEffect(() => {
    if (isOpen && previousAliasRef.current !== activeAlias) {
      onClose();
    }
    previousAliasRef.current = activeAlias;
  }, [activeAlias, isOpen, onClose]);

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData({
          ...initialData,
          frameworkS3Key: initialData.frameworkS3Key || null,
          frameworkFileName: initialData.frameworkFileName || null,
        });
        setOriginalClauses(initialData.clauses);
        setOriginalFrameworkKey(initialData.frameworkS3Key || null);
        setOriginalFrameworkFileName(initialData.frameworkFileName || null);
      } else {
        setFormData(defaultFormData);
        setOriginalClauses([]);
        setOriginalFrameworkKey(null);
        setOriginalFrameworkFileName(null);
      }
      setDropdownOpen(false);
      setSearchQuery('');
      setError(null);
      setIsUploading(false);
      setIsInReplaceMode(false);
      setShowFrameworkConfirm(false);
    }
  }, [isOpen, mode, initialData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery('');
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Calculate which clauses can still be added (filtered by search query)
  const addableClauses = useMemo(() => {
    const selectedIds = new Set(formData.clauses.map(c => c.id));
    const available = availableClauses.filter(c => !selectedIds.has(c.id));

    if (!searchQuery.trim()) {
      return available;
    }

    const query = searchQuery.toLowerCase().trim();
    return available.filter(c => c.name.toLowerCase().includes(query));
  }, [availableClauses, formData.clauses, searchQuery]);

  const updateField = useCallback(<K extends keyof ContractTypeFormData>(
    field: K,
    value: ContractTypeFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formData.clauses.findIndex(c => String(c.id) === active.id);
      const newIndex = formData.clauses.findIndex(c => String(c.id) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        updateField('clauses', arrayMove(formData.clauses, oldIndex, newIndex));
      }
    }
  }, [formData.clauses, updateField]);

  const handleAddClause = useCallback((clause: ClauseListItem) => {
    updateField('clauses', [...formData.clauses, { id: clause.id, name: clause.name }]);
    setDropdownOpen(false);
    setSearchQuery('');
  }, [formData.clauses, updateField]);

  // Handle creating a new clause from within this modal
  const handleCreateClauseSuccess = useCallback((clauseResult: ClauseTypeResult) => {
    // Add the new clause with its real ID
    updateField('clauses', [...formData.clauses, { id: clauseResult.id, name: clauseResult.name }]);
    setCreateClauseModalOpen(false);
    setDropdownOpen(false);
    setSearchQuery('');
    // Notify parent to refresh clauses list
    onClausesRefreshNeeded?.();
  }, [formData.clauses, updateField, onClausesRefreshNeeded]);

  const handleOpenCreateClause = useCallback(() => {
    setCreateClauseModalOpen(true);
  }, []);

  const handleRemoveClause = useCallback((clauseId: number) => {
    updateField('clauses', formData.clauses.filter(c => c.id !== clauseId));
  }, [formData.clauses, updateField]);

  // Compute clause_type_associations for the API
  const computeClauseAssociations = useCallback(() => {
    const associations: api.ClauseTypeAssociation[] = [];
    const originalIds = new Set(originalClauses.map(c => c.id));
    const newIds = new Set(formData.clauses.map(c => c.id));

    // Removed clauses
    for (const clause of originalClauses) {
      if (!newIds.has(clause.id)) {
        associations.push({
          clause_type_id: clause.id,
          order: 0,
          action: 'remove'
        });
      }
    }

    // Added or updated clauses
    formData.clauses.forEach((clause, index) => {
      const order = index + 1;
      if (!originalIds.has(clause.id)) {
        // New clause
        associations.push({
          clause_type_id: clause.id,
          order,
          action: 'add'
        });
      } else {
        // Existing clause - update order
        associations.push({
          clause_type_id: clause.id,
          order,
          action: 'update'
        });
      }
    });

    return associations;
  }, [originalClauses, formData.clauses]);

  const handleSubmit = useCallback(async (skipConfirm = false) => {
    if (!formData.name.trim()) {
      setError('Le nom du contrat est requis');
      return;
    }

    // Validate framework is always required
    if (!formData.frameworkS3Key) {
      setError('Le document cadre est requis');
      return;
    }

    // Check if user clicked "replace" but didn't upload a new file (edit mode only)
    if (skipConfirm !== true && mode === 'edit' && isInReplaceMode && formData.frameworkS3Key === originalFrameworkKey) {
      setShowFrameworkConfirm(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let response;

      if (mode === 'edit' && contractId) {
        const associations = computeClauseAssociations();

        // Only include framework_s3_key if it changed
        const frameworkChanged = formData.frameworkS3Key !== originalFrameworkKey;

        response = await api.updateContract(contractId, {
          name: formData.name,
          note: cleanEmptyHtml(formData.directives),
          clause_type_associations: associations.length > 0 ? associations : undefined,
          framework_s3_key: frameworkChanged ? formData.frameworkS3Key : undefined,
        });
      } else {
        response = await api.createContract({
          name: formData.name,
          note: cleanEmptyHtml(formData.directives),
          framework_s3_key: formData.frameworkS3Key!,
          clause_type_associations: formData.clauses.map((c, index) => ({
            clause_type_id: c.id,
            order: index + 1
          })),
        });
      }

      if (response.success && response.data) {
        logger.info(`Contrat ${mode === 'edit' ? 'modifié' : 'créé'}: ${response.data.id}`);
        onSubmitSuccess?.({
          id: response.data.id,
          name: response.data.name,
          note: response.data.note,
          clause_types: response.data.clause_types.map(c => ({
            id: c.id,
            name: c.name,
            order: c.order ?? undefined,
          })),
        });
        onRefreshNeeded?.();
        onClose();
      } else {
        setError(response.error || 'Erreur lors de la sauvegarde');
        logger.error('Erreur sauvegarde contrat:', response.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      logger.error('Erreur sauvegarde contrat:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, contractId, formData, originalFrameworkKey, isInReplaceMode, computeClauseAssociations, onClose, onSubmitSuccess, onRefreshNeeded]);

  const handleConfirmKeepFramework = useCallback(() => {
    setShowFrameworkConfirm(false);
    handleSubmit(true);
  }, [handleSubmit]);

  const handleCancelConfirm = useCallback(() => {
    setShowFrameworkConfirm(false);
  }, []);

  const title = mode === 'add' ? 'Ajouter un contrat' : 'Modifier le contrat';

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitDisabled={!formData.name.trim() || isUploading || !formData.frameworkS3Key}
      >
        {error && (
          <div className="form-error-message">
            {error}
          </div>
        )}

        <div className="form-field">
          <label htmlFor="contract-name" className="form-field-label">
            Nom du contrat *
          </label>
          <input
            id="contract-name"
            type="text"
            className="form-field-input"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Ex: Contrat de prestation de services"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-field">
          <label className="form-field-label">
            Directives
          </label>
          <RichTextEditor
            value={formData.directives}
            onChange={(html) => updateField('directives', html)}
            placeholder="Saisissez les directives pour ce type de contrat..."
          />
        </div>

        <FileUploadInput
          id="framework-upload"
          label="Document cadre (Framework)"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          required
          existingFileKey={originalFrameworkKey}
          existingFileName={originalFrameworkFileName}
          disabled={isSubmitting || isUploading}
          onChange={(fileKey) => updateField('frameworkS3Key', fileKey)}
          onUploadStateChange={setIsUploading}
          onReplaceModeChange={setIsInReplaceMode}
          infoContent={
            <>
              <p><strong>Document cadre</strong></p>
              <p>
                Uploadez le fichier DOCX qui servira de modèle de base
                pour la génération de ce type de contrat.
              </p>
              <p>
                Ce document doit contenir la structure générale du contrat avec des "blobs" au format "[●nom du blob à remplir●]".
              </p>
              <p>
                Il doit également contenir une seule fois les blobs suivants : "[●clause_title●]" et "[●clause_content●]" à l'endroit où les clauses sont attendues.
              </p>
              <p>
                Les blobs servent à la fois pour déterminer la position, mais aussi la mise en page des éléments dans le document.
              </p>
            </>
          }
        />

        <div className="form-section">
          <div className="clause-section-header">
            <h3 className="form-section-title">Clauses associées</h3>
            <div className="clause-add-dropdown-container" ref={dropdownRef}>
              <button
                type="button"
                className="clause-add-btn-compact"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                title="Ajouter une clause"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Ajouter
              </button>

              {dropdownOpen && (
                <div className="clause-dropdown-search">
                  <div className="clause-dropdown-search-header">
                    <svg className="clause-dropdown-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="clause-dropdown-search-input"
                      placeholder="Rechercher une clause..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="clause-dropdown-list">
                    {addableClauses.length > 0 ? (
                      addableClauses.map((clause) => (
                        <div
                          key={clause.id}
                          className="clause-dropdown-item"
                          onClick={() => handleAddClause(clause)}
                        >
                          {clause.name}
                        </div>
                      ))
                    ) : (
                      <div className="clause-dropdown-empty">
                        {searchQuery.trim() ? 'Aucune clause trouvée' : 'Toutes les clauses ont été ajoutées'}
                      </div>
                    )}
                  </div>
                  <div className="clause-dropdown-footer">
                    <button
                      type="button"
                      className="clause-create-btn"
                      onClick={handleOpenCreateClause}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Créer une clause
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="clause-list-container">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={formData.clauses.map(c => String(c.id))}
                strategy={verticalListSortingStrategy}
              >
                <div data-testid="droppable-clauses">
                  {formData.clauses.length === 0 ? (
                    <div className="clause-list-empty">
                      Aucune clause associée
                    </div>
                  ) : (
                    formData.clauses.map((clause, index) => (
                      <SortableClauseItem
                        key={clause.id}
                        clause={clause}
                        index={index}
                        onRemove={handleRemoveClause}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        <ClauseTypeModal
          isOpen={createClauseModalOpen}
          onClose={() => setCreateClauseModalOpen(false)}
          mode="add"
          onSubmitSuccess={handleCreateClauseSuccess}
        />
      </FormModal>

      {/* Confirmation dialog for keeping existing framework */}
      {showFrameworkConfirm && (
        <div className="form-modal-overlay" style={{ zIndex: 1100 }}>
          <div className="confirm-dialog">
            <h3 className="confirm-dialog-title">Information</h3>
            <p>En l'absence de nouveau document cadre, le précédent sera conservé pour ce contrat.</p>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="confirm-dialog-btn confirm-dialog-btn-secondary"
                onClick={handleCancelConfirm}
              >
                Annuler
              </button>
              <button
                type="button"
                className="confirm-dialog-btn confirm-dialog-btn-primary"
                onClick={handleConfirmKeepFramework}
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
